import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { X, Plane, Calendar, MapPin, Users, Loader2, Save, Check } from 'lucide-react';
import CityAutocomplete from './CityAutocomplete';

/**
 * Modern theme trip edit modal with dark styling.
 * Allows editing title, date range, destination, and multiple executive assignment.
 */
const EditTripModal2 = ({ trip, onClose, onTripUpdated, executives = [] }) => {
  const [tripTitle, setTripTitle] = useState(trip?.title || '');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [destination, setDestination] = useState(trip?.destination || '');
  const [selectedExecutiveIds, setSelectedExecutiveIds] = useState([]);
  // Per-exec access level: { [execId]: 'none' | 'read' | 'write' }. Default 'none'.
  const [execAccessLevels, setExecAccessLevels] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggleExecutive = (execId) => {
    setSelectedExecutiveIds(prev => {
      const next = prev.includes(execId)
        ? prev.filter(id => id !== execId)
        : [...prev, execId];
      // Removing an exec also clears any access grant for them
      if (!next.includes(execId)) {
        setExecAccessLevels(levels => {
          const { [execId]: _, ...rest } = levels;
          return rest;
        });
      }
      return next;
    });
  };

  const setExecAccessLevel = (execId, level) => {
    setExecAccessLevels(prev => ({ ...prev, [execId]: level }));
  };

  const getExecutiveName = (exec) => {
    return exec.full_name || `${exec.first_name || ''} ${exec.last_name || ''}`.trim() || 'Unnamed Executive';
  };

  useEffect(() => {
    if (trip) {
      setTripTitle(trip.title || '');
      setDestination(trip.destination || '');

      // Parse dates - handle both ISO strings and date objects
      if (trip.start_date) {
        const startDateStr = typeof trip.start_date === 'string'
          ? trip.start_date.split('T')[0]
          : trip.start_date;
        setStartDate(startDateStr);
      }
      if (trip.end_date) {
        const endDateStr = typeof trip.end_date === 'string'
          ? trip.end_date.split('T')[0]
          : trip.end_date;
        setEndDate(endDateStr);
      }

      // Set executives if trip has any assigned
      if (trip.executive_ids && trip.executive_ids.length > 0) {
        setSelectedExecutiveIds(trip.executive_ids);
      } else {
        setSelectedExecutiveIds([]);
      }

      // Hydrate per-exec access levels from trip.executive_permissions
      const levels = {};
      (trip.executive_permissions || []).forEach(ep => {
        if (ep && ep.executive_id && (ep.level === 'read' || ep.level === 'write')) {
          levels[ep.executive_id] = ep.level;
        }
      });
      setExecAccessLevels(levels);
    }
  }, [trip]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tripTitle.trim()) {
      setError('Trip title is required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      const executive_permissions = selectedExecutiveIds
        .map(execId => ({ executive_id: execId, level: execAccessLevels[execId] }))
        .filter(ep => ep.level === 'read' || ep.level === 'write');

      const payload = {
        title: tripTitle.trim(),
        start_date: startDate || null,
        end_date: endDate || null,
        destination: destination || null,
        executive_ids: selectedExecutiveIds,
        executive_permissions
      };

      const response = await axios.put(`${apiBase}/trips/${trip.id}`, payload, { headers });

      if (onTripUpdated) {
        onTripUpdated(response.data);
      }

      onClose();
    } catch (error) {
      console.error('Failed to update trip:', error);
      setError(error.response?.data?.detail || 'Failed to update trip. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!trip) return null;

  return (
    <div className="fixed inset-0 bg-ui-overlay/60 backdrop-blur-sm overflow-y-auto h-full w-full z-[70] flex items-center justify-center p-4">
      <div className="relative w-full max-w-lg bg-ui-raised border border-ui-divider rounded-2xl shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-ui-divider">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-ui-green-strong to-ui-teal-strong">
              <Plane className="w-5 h-5 text-ui-on-primary" />
            </div>
            <h2 className="text-xl font-semibold text-ui-ink">Edit Trip Details</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-ui-ink-3 hover:text-ui-ink hover:bg-ui-hover transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Trip Title */}
          <div>
            <label className="block text-sm font-medium text-ui-ink-2 mb-2">
              Trip Title <span className="text-ui-red-fg">*</span>
            </label>
            <input
              type="text"
              value={tripTitle}
              onChange={(e) => setTripTitle(e.target.value)}
              className="w-full bg-ui-sunk border border-ui-control rounded-xl px-4 py-3 text-ui-ink placeholder-ui-faint focus:outline-none focus:ring-2 focus:ring-ui-green-solid focus:border-transparent transition-all"
              placeholder="e.g., NYC Business Conference"
              required
              autoFocus
            />
          </div>

          {/* Destination */}
          <div>
            <label className="block text-sm font-medium text-ui-ink-2 mb-2">
              <MapPin className="w-4 h-4 inline mr-1" />
              Destination <span className="text-ui-muted text-xs font-normal">(optional)</span>
            </label>
            <CityAutocomplete
              value={destination}
              onChange={setDestination}
              placeholder="Search for a city..."
              className="w-full bg-ui-sunk border border-ui-control rounded-xl px-4 py-3 text-ui-ink placeholder-ui-faint focus:outline-none focus:ring-2 focus:ring-ui-green-solid focus:border-transparent transition-all"
            />
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-ui-ink-2 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Start Date <span className="text-ui-muted text-xs font-normal">(optional)</span>
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full bg-ui-sunk border border-ui-control rounded-xl px-4 py-3 text-ui-ink focus:outline-none focus:ring-2 focus:ring-ui-green-solid focus:border-transparent transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-ui-ink-2 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                End Date <span className="text-ui-muted text-xs font-normal">(optional)</span>
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate || undefined}
                className="w-full bg-ui-sunk border border-ui-control rounded-xl px-4 py-3 text-ui-ink focus:outline-none focus:ring-2 focus:ring-ui-green-solid focus:border-transparent transition-all"
              />
            </div>
          </div>

          {/* Executive Selection - Multiple */}
          {executives.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-ui-ink-2 mb-2">
                <Users className="w-4 h-4 inline mr-1" />
                Executives <span className="text-ui-muted text-xs font-normal">(optional)</span>
              </label>
              <div className="bg-ui-sunk border border-ui-control rounded-xl p-2 max-h-72 overflow-y-auto">
                {executives.map((exec) => {
                  const isSelected = selectedExecutiveIds.includes(exec.id);
                  const accessLevel = execAccessLevels[exec.id] || 'none';
                  return (
                    <div
                      key={exec.id}
                      className={`rounded-lg transition-colors ${
                        isSelected ? 'bg-ui-green-bg' : 'hover:bg-ui-hover'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => toggleExecutive(exec.id)}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                          isSelected ? 'text-ui-ink' : 'text-ui-ink-2'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          isSelected
                            ? 'bg-ui-green-strong border-ui-green-solid'
                            : 'border-ui-control'
                        }`}>
                          {isSelected && <Check className="w-3 h-3 text-ui-on-primary" />}
                        </div>
                        <span className="text-sm">{getExecutiveName(exec)}</span>
                      </button>
                      {isSelected && (
                        <div className="px-3 pb-2 pl-11 flex items-center gap-2">
                          <span className="text-xs text-ui-ink-3">Dashboard access:</span>
                          <div className="inline-flex rounded-md border border-ui-control overflow-hidden text-xs">
                            {[
                              { value: 'none', label: 'None' },
                              { value: 'read', label: 'Read' },
                              { value: 'write', label: 'Write' }
                            ].map(opt => {
                              const active = accessLevel === opt.value;
                              return (
                                <button
                                  key={opt.value}
                                  type="button"
                                  onClick={() => setExecAccessLevel(exec.id, opt.value)}
                                  className={`px-2.5 py-1 transition-colors ${
                                    active
                                      ? 'bg-ui-green-strong text-ui-on-primary'
                                      : 'bg-ui-sunk text-ui-ink-2 hover:bg-ui-hover'
                                  }`}
                                  aria-pressed={active}
                                >
                                  {opt.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {selectedExecutiveIds.length > 0 && (
                <div className="mt-2 text-xs text-ui-green-fg">
                  {selectedExecutiveIds.length} executive{selectedExecutiveIds.length > 1 ? 's' : ''} selected
                </div>
              )}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-ui-red-bg border border-ui-red-line rounded-xl p-4 text-sm text-ui-red-fg">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-5 py-2.5 rounded-xl text-ui-ink-2 bg-ui-sunk hover:bg-ui-hover border border-ui-control transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !tripTitle.trim()}
              className="px-5 py-2.5 rounded-xl text-ui-on-primary bg-gradient-to-r from-ui-green-strong to-ui-teal-strong hover:from-ui-green-strong/90 hover:to-ui-teal-strong/90 transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTripModal2;
